import { useState, useMemo, useRef, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    Button,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    Input,
} from "@heroui/react";
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableColumn,
} from "@heroui/react";
import { getKeyValue, SortDescriptor, Spinner } from "@heroui/react";
import useRequest from "@/lib/request/useRequest";
import { createFileRoute } from "@tanstack/react-router";
import { InferType } from "yup";
import SelectInput from "@/components/inputs/SelectInput";
import TextInput from "@/components/inputs/TextInput";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { FertilizerType } from "@/types/products";
import {
    faPlus,
    faEdit,
    faTrash,
    faTint,
    faLeaf,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/dashboard/account-settings/products")({
    component: Products,
});

interface Product {
    id: number;
    name: string;
    fertilizer_type: FertilizerType;
    density: number | null;
    lbs_N_per_gallon: number | null;
    percent_N: number | null;
    created_at?: string;
    updated_at?: string;
    is_active?: boolean;
    organization?: number;
}

const schema = yup.object().shape({
    name: yup.string().required("Name is required"),
    fertilizer_type: yup
        .string()
        .oneOf([String(FertilizerType.Dry), String(FertilizerType.Liquid)])
        .required("Type is required"),
    density: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        ),
    lbs_N_per_gallon: yup
        .number()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        ),
    percent_N: yup
        .number()
        .required("Percent N is required")
        .transform((value, originalValue) =>
            originalValue === "" ? null : value,
        ),
});

type FormData = InferType<typeof schema>;

function Products() {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(
        null,
    );
    const [filterLiquid, setFilterLiquid] = useState(false);
    const [filterDry, setFilterDry] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: "name",
        direction: "ascending",
    });
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [tableHeight, setTableHeight] = useState(550);
    const { useGetRequest, useMutateRequest } = useRequest();
    const {
        data: products,
        isLoading,
        error,
        refetch,
    } = useGetRequest<Product[]>({
        url: "/fertilizers/",
    });

    useEffect(() => {
        if (tableContainerRef.current) {
            setTableHeight(tableContainerRef.current.clientHeight);
        }
    }, [isLoading]);

    const transformedProducts = useMemo(() => {
        return products?.map((product) => ({
            ...product,
            fertilizer_type: Number(product.fertilizer_type) as FertilizerType,
        }));
    }, [products]);

    const mutateProduct = useMutateRequest({
        successMessage: t("products.product_updated_successfully", {
            ns: "accountSettings",
        }),
        errorMessage: t("products.failed_to_update_product", {
            ns: "accountSettings",
        }),
    });

    const deleteProduct = useMutateRequest({
        successMessage: t("products.product_deleted_successfully", {
            ns: "accountSettings",
        }),
        errorMessage: t("products.failed_to_delete_product", {
            ns: "accountSettings",
        }),
    });

    const form = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            name: "",
            fertilizer_type: String(FertilizerType.Liquid),
            density: null,
            lbs_N_per_gallon: null,
            percent_N: 0,
        },
    });

    const COLUMNS = [
        { key: "name", label: t("products.name", { ns: "accountSettings" }) },
        {
            key: "fertilizer_type",
            label: t("products.type", { ns: "accountSettings" }),
        },
        {
            key: "density",
            label: t("products.density", { ns: "accountSettings" }),
        },
        {
            key: "lbs_N_per_gallon",
            label: t("products.lbs_n_gal", { ns: "accountSettings" }),
        },
        {
            key: "percent_N",
            label: t("products.percent_n", { ns: "accountSettings" }),
        },
        {
            key: "actions",
            label: t("products.actions", { ns: "accountSettings" }),
        },
    ];

    const filteredProducts = transformedProducts?.filter((product: Product) => {
        const matchesSearch = product.name
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        const matchesType =
            (filterLiquid &&
                product.fertilizer_type === FertilizerType.Liquid) ||
            (filterDry && product.fertilizer_type === FertilizerType.Dry) ||
            (!filterLiquid && !filterDry);
        return matchesSearch && matchesType;
    });

    const sortedProducts = useMemo(() => {
        if (!filteredProducts) return [];
        return sortArrayByKey(
            filteredProducts,
            sortDescriptor.column as keyof Product,
            sortDescriptor.direction === "ascending" ? "asc" : "desc",
        );
    }, [filteredProducts, sortDescriptor]);

    const handleEdit = (product: Product) => {
        setSelectedProduct(product);
        form.reset({
            ...product,
            fertilizer_type: String(product.fertilizer_type),
            percent_N: product.percent_N ?? 0,
        });
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setSelectedProduct(null);
        form.reset({
            name: "",
            fertilizer_type: String(FertilizerType.Liquid),
            density: null,
            lbs_N_per_gallon: null,
            percent_N: 0,
        });
        setIsModalOpen(true);
    };

    const onSubmit = async (data: FormData) => {
        const isEdit = !!selectedProduct;
        const url = isEdit
            ? `/fertilizers/${selectedProduct.id}/`
            : "/fertilizers/";
        const method = isEdit ? "put" : "post";
        const body = isEdit
            ? {
                  ...data,
                  id: selectedProduct.id,
                  fertilizer_type: Number(data.fertilizer_type),
              }
            : { ...data, fertilizer_type: Number(data.fertilizer_type) };

        await mutateProduct.mutateAsync({
            url,
            method,
            data: body,
        });

        form.reset();
        setSelectedProduct(null);
        setIsModalOpen(false);
        await refetch();
    };

    const handleDelete = async () => {
        if (!selectedProduct) return;
        await deleteProduct.mutateAsync({
            url: `/fertilizers/${selectedProduct.id}/`,
            method: "delete",
        });
        setIsModalOpen(false);
        await refetch();
    };

    const handleModalClose = () => {
        form.reset();
        setSelectedProduct(null);
        setIsModalOpen(false);
    };

    if (error)
        return (
            <div>
                {t("products.error_loading_products", {
                    ns: "accountSettings",
                })}
            </div>
        );

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-4 p-4 pb-1">
                <h1 className="text-2xl font-bold">
                    {t("products.title", { ns: "accountSettings" })}
                </h1>
                <Button
                    color="primary"
                    onPress={handleAdd}
                    startContent={
                        <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                    }
                >
                    {t("products.add_new_product", { ns: "accountSettings" })}
                </Button>
            </div>

            <div className="flex gap-2 mb-4 p-4 pt-2 justify-between">
                <div>
                    <Input
                        placeholder={t("products.search_products", {
                            ns: "accountSettings",
                        })}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    <Button
                        color={filterLiquid ? "primary" : "default"}
                        variant={filterLiquid ? "solid" : "flat"}
                        onPress={() => setFilterLiquid(!filterLiquid)}
                    >
                        {t("products.liquid", { ns: "accountSettings" })}
                    </Button>
                    <Button
                        color={filterDry ? "primary" : "default"}
                        variant={filterDry ? "solid" : "flat"}
                        onPress={() => setFilterDry(!filterDry)}
                    >
                        {t("products.dry", { ns: "accountSettings" })}
                    </Button>
                </div>
            </div>

            <div ref={tableContainerRef} className="overflow-scroll h-full">
                <Table
                    isHeaderSticky
                    isVirtualized
                    aria-label="Products"
                    onSortChange={setSortDescriptor}
                    sortDescriptor={sortDescriptor}
                    maxTableHeight={tableHeight}
                    style={{ tableLayout: "fixed" }}
                    classNames={{
                        th: "px-[0.25rem]",
                        td: "px-[0.25rem]",
                        wrapper: "p-0 shadow-none rounded-none",
                        base: "h-full",
                    }}
                >
                    <TableHeader columns={COLUMNS}>
                        {(column) => (
                            <TableColumn
                                key={column.key}
                                allowsSorting={column.key !== "actions"}
                            >
                                {column.label}
                            </TableColumn>
                        )}
                    </TableHeader>
                    <TableBody
                        items={sortedProducts}
                        emptyContent={t("products.no_products_found", {
                            ns: "accountSettings",
                        })}
                        isLoading={isLoading}
                        loadingContent={
                            <Spinner
                                label={t("products.loading", {
                                    ns: "accountSettings",
                                })}
                            />
                        }
                    >
                        {(product) => (
                            <TableRow
                                key={product.id}
                                className="hover:bg-gray-100 rounded-lg"
                            >
                                {(columnKey) => {
                                    if (columnKey === "actions") {
                                        return (
                                            <TableCell>
                                                <Button
                                                    variant="flat"
                                                    onPress={() =>
                                                        handleEdit(product)
                                                    }
                                                    startContent={
                                                        <FontAwesomeIcon
                                                            icon={faEdit}
                                                            className="h-4 w-4"
                                                        />
                                                    }
                                                >
                                                    {t("products.edit", {
                                                        ns: "accountSettings",
                                                    })}
                                                </Button>
                                            </TableCell>
                                        );
                                    }
                                    if (columnKey === "fertilizer_type") {
                                        return (
                                            <TableCell>
                                                {product.fertilizer_type ===
                                                FertilizerType.Liquid ? (
                                                    <>
                                                        <FontAwesomeIcon
                                                            icon={faTint}
                                                            className="text-blue-500 mr-1"
                                                        />
                                                        {t("products.liquid", {
                                                            ns: "accountSettings",
                                                        })}
                                                    </>
                                                ) : (
                                                    <>
                                                        <FontAwesomeIcon
                                                            icon={faLeaf}
                                                            className="text-green-600 mr-1"
                                                        />
                                                        {t("products.dry", {
                                                            ns: "accountSettings",
                                                        })}
                                                    </>
                                                )}
                                            </TableCell>
                                        );
                                    }
                                    return (
                                        <TableCell>
                                            {getKeyValue(product, columnKey) ??
                                                "-"}
                                        </TableCell>
                                    );
                                }}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <Modal isOpen={isModalOpen} onClose={handleModalClose} size="lg">
                <ModalContent>
                    <ModalHeader>
                        {selectedProduct
                            ? t("products.edit_product", {
                                  ns: "accountSettings",
                              })
                            : t("products.add_new_product", {
                                  ns: "accountSettings",
                              })}
                    </ModalHeader>
                    <ModalBody>
                        <FormProvider {...form}>
                            <form
                                onSubmit={form.handleSubmit(onSubmit)}
                                className="space-y-4 p-4"
                            >
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.name", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <TextInput name="name" />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.type", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <SelectInput
                                        name="fertilizer_type"
                                        options={[
                                            {
                                                id: String(
                                                    FertilizerType.Liquid,
                                                ),
                                                description: t(
                                                    "products.liquid",
                                                    { ns: "accountSettings" },
                                                ),
                                            },
                                            {
                                                id: String(FertilizerType.Dry),
                                                description: t("products.dry", {
                                                    ns: "accountSettings",
                                                }),
                                            },
                                        ]}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.density", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <TextInput
                                        type="number"
                                        step="0.01"
                                        name="density"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.lbs_n_gal", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <TextInput
                                        type="number"
                                        step="0.01"
                                        name="lbs_N_per_gallon"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t("products.percent_n", {
                                            ns: "accountSettings",
                                        })}
                                    </label>
                                    <TextInput
                                        type="number"
                                        step="0.01"
                                        name="percent_N"
                                        required
                                    />
                                </div>

                                <div className="flex justify-end gap-2">
                                    {selectedProduct && (
                                        <Button
                                            color="danger"
                                            variant="flat"
                                            onPress={handleDelete}
                                            isLoading={mutateProduct.isPending}
                                            isDisabled={mutateProduct.isPending}
                                            className="mr-auto"
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    className="h-4 w-4"
                                                />
                                            }
                                        >
                                            {t("products.delete", {
                                                ns: "accountSettings",
                                            })}
                                        </Button>
                                    )}
                                    <Button
                                        variant="flat"
                                        onPress={handleModalClose}
                                        disabled={mutateProduct.isPending}
                                    >
                                        {t("products.cancel", {
                                            ns: "accountSettings",
                                        })}
                                    </Button>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        isLoading={mutateProduct.isPending}
                                        isDisabled={mutateProduct.isPending}
                                    >
                                        {t("products.save", {
                                            ns: "accountSettings",
                                        })}
                                    </Button>
                                </div>
                            </form>
                        </FormProvider>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    );
}
